import React from 'react';
import {Box, Flex, HStack, Image, Text, VStack} from '@chakra-ui/react';
import {LigneDeDevisDesc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import {useClient} from '@/haligator-factory';
import {routes, useVignetteArticle} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {useHalState} from '@synako/haligator';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {ArticleCodesDisplay} from '../panier/components/ArticleCodesDisplay';
import {DisplayLabels} from '../panier/PanierFound/DisplayLabels';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleNoteCommercial} from './ModaleNoteCommercial';
import {FaceIcon} from '@/components/svg/FaceIcon';
import {useNavigate} from 'react-router-dom';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import ColdIcon from '@/components/svg/ColdIcon';

// Composant pour afficher une ligne de devis dans l'onglet 'Devis personnalisé' de 'Mes Listes'
export const DevisLineItemClient: React.FC<{
  ligneDeDevisState: State<LigneDeDevisDesc>;
  lignePanier?: State<LigneDePanierDesc>;
}> = ({ligneDeDevisState, lignePanier}) => {
  const client = useClient();
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();

  // Récupération des données de la ligne
  const ligne = ligneDeDevisState.data;

  // Récupération de l'article pour la vignette
  const articleNav = ligneDeDevisState.follow('article');
  const vignetteUrl = useVignetteArticle(articleNav);
  const articleState = useHalState(articleNav);

  // Récupération de l'item catalogue pour les boutons d'achat
  const itemNav = client.follow('go:item_catalogue_2', {
    marketplace_id: 'FF',
    code: ligne.code_article,
  });
  const itemState = useHalState(itemNav);

  // Utilisation du prix du catalogue
  const prixCatalogueActuel = itemState.data?.prix_unitaire_ht;

  // Si les données de l'item ne sont pas encore chargées, on n'affiche pas les boutons d'achat
  const {buyBlock, uniteQuantiteBlock} = useBuyingBlocks(
    textToast,
    'liste',
    itemState.state,
    lignePanier,
  );

  // Navigation vers la fiche produit
  const routeToArticle = buildClientRoute(routes.client.produit, {
    code_article: ligne.code_article,
  });
  const canNavigate = !!itemState.data;

  const handleProductClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (canNavigate) {
      navigate(routeToArticle);
    }
  };

  // Récupération de la température de livraison
  const temperatureTournee = articleState.data?.temperature_livraison;

  // Bloc image produit
  const image = (
    <Box
      minWidth='80px'
      width='80px'
      height='80px'
      display='flex'
      alignItems='center'
      justifyContent='center'
      cursor={canNavigate ? 'pointer' : 'default'}
      onClick={handleProductClick}
      position='relative'
    >
      {vignetteUrl && (
        <Image
          src={vignetteUrl}
          alt={ligne.libelle}
          maxHeight='80px'
          maxWidth='80px'
          objectFit='contain'
          borderRadius='md'
        />
      )}
      {temperatureTournee === 'negatif' && (
        <Box
          position='absolute'
          bottom='5px'
          left='5px'
        >
          <ColdIcon boxSize='20px' />
        </Box>
      )}
    </Box>
  );

  const noteCommercial = ligne.note_article && (
    <HStack
      mt={1}
      width='100%'
      alignItems='start'
    >
      <Box
        background={'white'}
        height={25}
        w={25}
        p={1}
        pt={0}
        borderRadius='4px'
        alignItems='center'
        justifyContent='center'
      >
        <FaceIcon
          width='16px'
          height='16px'
          color='blue.main'
        />
      </Box>

      <VStack
        spacing={0}
        align='start'
        flex='1'
      >
        <Text
          fontSize='sm'
          color='blue.main'
          fontWeight='600'
          mb={1}
          mt='2px'
        >
          {t('liste.devis_personnalise.note_commercial')}
        </Text>
        <Text
          fontSize='sm'
          color='blue.main'
          fontStyle='italic'
          noOfLines={2}
          cursor='pointer'
          _hover={{textDecoration: 'underline'}}
          onClick={() =>
            NiceModal.show(ModaleNoteCommercial, {note: ligne.note_article})
          }
        >
          &quot;{ligne.note_article}&quot;
        </Text>
      </VStack>
    </HStack>
  );

  // Infos produit
  const infosProduit = (
    <VStack
      align='start'
      spacing={0}
      width='100%'
    >
      <HStack
        alignContent='baseline'
        spacing={2}
      >
        <Box
          cursor={canNavigate ? 'pointer' : 'default'}
          onClick={handleProductClick}
        >
          <ArticleCodesDisplay code_article={ligne.code_article} />
        </Box>
        <DisplayLabels
          SIQO_et_regimes_alimentaires={
            articleState.data?.tolede.SIQO_et_regimes_alimentaires
          }
        />
      </HStack>
      <Text
        fontWeight='600'
        fontSize='md'
        color='black.text'
        noOfLines={2}
        cursor={canNavigate ? 'pointer' : 'default'}
        onClick={handleProductClick}
      >
        {ligne.libelle}
      </Text>

      {/* Marque - Desktop */}
      <Text
        display={{base: 'none', md: 'block'}}
        lineHeight='16px'
        maxH='32px'
        color='gray.dark'
        cursor='pointer'
        _hover={{color: 'black.text'}}
        onClick={handleProductClick}
      >
        {articleState.data?.tolede.marque?.[0]}
      </Text>

      {/* Section note du commercial avec troncature et modale */}
      <Flex display={{base: 'none', md: 'flex'}}>{noteCommercial}</Flex>
    </VStack>
  );

  // Bloc prix catalogue
  const prixCatalogue =
    prixCatalogueActuel && ligne.unite_de_facturation ? (
      <PrixBlockUnitaire
        prixUnitaire={prixCatalogueActuel}
        prixUnitaireTtc={itemState.data?.prix_unitaire_ttc}
        uniteFacturation={ligne.unite_de_facturation}
        tauxTVA={itemState.data?.tva}
        style={{h: '100%', mt: {base: '0', md: '15px'}}}
      />
    ) : null;

  // Bloc boutons d'action (unité + panier + liste)
  const boutonsAction = (
    <HStack
      alignItems='start'
      ml='auto'
      mt={{base: '15px', md: 0}}
      justifyContent='space-between'
      width={{base: '100%', md: 'auto'}}
    >
      <Box display={{base: 'none', md: 'block'}}>{prixCatalogue}</Box>
      <Flex
        flexDirection={{base: 'column', md: 'row'}}
        w='100%'
      >
        <HStack>
          <Box
            mt={{base: '0', md: '15px'}}
            ml='0'
            mb={{base: '10px', md: 0}}
            flex='1 1 auto'
            minW='fit-content'
            w='100%'
          >
            {/* Select d'unité de quantité */}
            {itemState.state &&
              uniteQuantiteBlock({
                minW: {base: '150px', md: '245px'},
                w: {base: '100%', md: '245px'},
              })}
          </Box>
          <Box
            display={{base: 'block', md: 'none'}}
            flex='0 1 auto'
            marginLeft='auto'
            ml='5px'
          >
            {prixCatalogue}
          </Box>
        </HStack>
        {/* Bloc pour afficher la note du commercial sur mobile */}
        {ligne.note_article && (
          <Flex
            display={{base: 'flex', md: 'none'}}
            align='center'
            justify='center'
            bg='blue.main_transparency_10'
            p='5px'
            borderRadius='8px'
            w='100%'
          >
            {noteCommercial}
          </Flex>
        )}
        <HStack
          w={{base: '100%', md: 'auto'}}
          ml={{base: 0, md: '5px'}}
        >
          {/* Boutons d'achat */}
          {itemState.state &&
            buyBlock({
              afficher_bouton_liste: true,
              minW: '280px',
              w: {base: '100%', md: '280px'},
            })}
        </HStack>
      </Flex>
    </HStack>
  );

  return (
    <Flex
      border='1px solid'
      borderColor='gray.light'
      bg='white'
      width='100%'
      alignItems='stretch'
      position='relative'
      p={{base: '2', md: '20px'}}
      mb='-1px'
    >
      {/* Version desktop */}
      <HStack
        display={{base: 'none', md: 'flex'}}
        w='100%'
        flexWrap='wrap'
        alignItems='start'
      >
        <HStack
          alignItems='start'
          flex='1'
          minW='300px'
        >
          {image}
          <Box
            flex='1'
            pl={4}
          >
            {infosProduit}
          </Box>
        </HStack>
        <Box
          flex='0 0 auto'
          minW='420px'
          ml='auto'
        >
          {boutonsAction}
        </Box>
      </HStack>

      {/* Version mobile */}
      <VStack
        display={{base: 'flex', md: 'none'}}
        alignItems='stretch'
        w='100%'
      >
        <HStack>
          {image}
          <Box
            flex='1'
            pl={4}
          >
            {infosProduit}
          </Box>
        </HStack>
        {boutonsAction}
      </VStack>
    </Flex>
  );
};
